import { useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { GET_WORKFLOW_VERSIONS_QUERY } from 'src/graphql/entities/workflow/workflow.operations';
import { GetWorkflowVersionsQuery } from 'src/graphql/entities/workflow/workflow.types';
import { QueryVariables } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

export const useWorkflowVersions = ({
  workflowId,
  includeDeleted = false,
}: {
  workflowId?: string | null;
  includeDeleted?: boolean;
}) => {
  const { showErrorAlert } = useNotifications();

  const {
    data,
    loading,
    error,
    refetch: refetchWorkflowVersions,
  } = useQuery<GetWorkflowVersionsQuery, QueryVariables>(GET_WORKFLOW_VERSIONS_QUERY, {
    variables: {
      params: {
        filters: workflowId
          ? [
              {
                attribute: 'workflowId',
                operator: '=',
                value: workflowId,
              },
            ]
          : [],
        includeDeleted,
        sorting: [{ attribute: 'version', desc: true }],
      },
    },
    skip: isNullish(workflowId),
    // Versions change from the actions menu on this very toolbar, and from the assignments
    // modal's repair path. Render the cache immediately, then correct it from the network,
    // so remounting the page cannot show a version list the server has moved past.
    fetchPolicy: 'cache-and-network',
    // Always platform scope, so a platform (partner 0) workflow's versions read from any partner;
    // the `ov-partner-ids` header otherwise carries the selected partner alone. Not decided per
    // workflow: whether it is a platform one is not known until the workflow list arrives, and
    // the scope rides in request context, outside the cache key, so a first read without it is
    // cached empty under the same variables. The `workflowId` filter keeps the result to that
    // one workflow, so the wider scope reveals nothing else.
    //
    // `ttl: 0` opts out of the 12h TTL link cache: without it a refetch() — after a version is
    // created, deleted or activated — is answered from the cache and never reaches the server, so
    // the page waits forever for a row the list will not show until a reload.
    context: { includePlatformLevelData: true, ttl: 0 },
    onError: (fetchError) => {
      console.error(fetchError);
      showErrorAlert('Failed to load workflow versions. Please try again.');
    },
  });

  const workflowVersions = useMemo(
    () => data?.getWorkflowVersion?.records ?? [],
    [data?.getWorkflowVersion?.records],
  );

  return {
    workflowVersions,
    loading,
    error,
    refetchWorkflowVersions,
  };
};
